import { KeyboardEvent, useCallback, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useTQC2070_R01Store, defaultTQC2070_R01Filter } from "./Store_TQC2070_R01";
import useUiuxStore from "src/store/useUiuxStore";
import { base64ToBlob, downloadExcel } from "src/utils/blob/blobUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import {
	createTQC2070_R01,
	deleteTQC2070_R01,
	downloadExcelSearch_TQC2070_R01,
	downloadPdfZinc_TQC2070_R01,
	downloadPdfNaoh_TQC2070_R01,
	getTQC2070_R01Search,
	updateTQC2070_R01,
} from "./Api_TQC2070_R01";
import { defaultTQC2070_R01Dto, TQC2070_R01Dto } from "./Dto_TQC2070_R01";
import { buildDefaultAnalysisDt, normalizeValueIndex } from "./Utils_TQC2070_R01";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC2070_R01Dto> | null>;
}

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const Content_TQC2070_R01 = (props: Props) => {
	const { gridRef } = props;

	useFetchActionbarAuth("TQC2070_R01");
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	const [clickable, setClickable] = useState(true);

	const {
		TQC2070_R01List,
		setTQC2070_R01List,
		insertTQC2070_R01List,
		selectedTQC2070_R01List,
		setSelectedTQC2070_R01List,
		removeSelectedTQC2070_R01List,
		editTQC2070_R01,
		setEditTQC2070_R01,
		TQC2070_R01Filter,
		setTQC2070_R01Filter,
		TQC2070_R01PastFilter,
		setTQC2070_R01PastFilter,
		originalTQC2070_R01List,
		setOriginalTQC2070_R01List,
		deletedTQC2070_R01List,
		addDeletedTQC2070_R01List,
		removeDeletedTQC2070_R01List,
		removeTQC2070_R01List,
		initData,
		setScrollTop,
	} = useTQC2070_R01Store();

	const { setMessage } = useUiuxStore();

	const fetchData = useCallback(async (arg?: FetchOptions | KeyboardEvent<HTMLInputElement>) => {
		const messageMode = arg && typeof arg === 'object' && 'messageMode' in arg
			? arg.messageMode ?? 'show'
			: 'show';
		setLoadingSpinner(true);
		if (messageMode === 'show') {
			setScrollTop(null);
		}

		const response = await getTQC2070_R01Search(TQC2070_R01Filter);
		const responseData = response.data.data as TQC2070_R01Dto[];

		const normalizedList = responseData.map((item, index) => {
			const fallbackIndex = index + 1;
			// 유틸 함수로 정규화
			const normalizedValueIndex = normalizeValueIndex(item.valueIndex, fallbackIndex);

			return {
				...defaultTQC2070_R01Dto,
				...item,
				valueIndex: normalizedValueIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : fallbackIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
				inserted: false,
				isModified: false,
			};
		});
		setTQC2070_R01List(normalizedList);

		// 원본 데이터 저장 (수정 여부 판단용)
		setOriginalTQC2070_R01List(normalizedList.map(item => ({ ...item })));

		if (normalizedList && normalizedList.length > 0) {
			setTQC2070_R01PastFilter(TQC2070_R01Filter);
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${normalizedList.length}건)`);
			}
		} else {
			// 데이터가 없으면 신규행 한 개 자동 추가
			const defaultAnalysisDt = buildDefaultAnalysisDt(TQC2070_R01Filter);
			const newRow: TQC2070_R01Dto = {
				...defaultTQC2070_R01Dto,
				analysisDt: defaultAnalysisDt,
				// valueIndex: '1',
				inserted: true,
				isModified: false,
			};

			insertTQC2070_R01List([newRow], 0);
			// 원본 데이터도 업데이트
			setOriginalTQC2070_R01List([{ ...newRow, inserted: false }]);
			if (messageMode === 'show') {
				showWarning('조회된 데이터가 없습니다.');
			}
		}
		setLoadingSpinner(false);

		return response.data.data;
	}, [TQC2070_R01Filter, setTQC2070_R01List, insertTQC2070_R01List, setTQC2070_R01PastFilter, setOriginalTQC2070_R01List, setLoadingSpinner, setMessage, setScrollTop]);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const response = await downloadExcelSearch_TQC2070_R01(TQC2070_R01Filter);
			const blob = base64ToBlob(
				response.data.data,
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			);
			downloadExcel(blob, '아연라인 액분석 결과.xlsx');
			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error) {
			console.error('엑셀 다운로드 실패:', error);
			showWarning('엑셀 다운로드에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2070_R01Filter, setLoadingSpinner]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleDeleteClick = useCallback(() => {
		if (deletedTQC2070_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedTQC2070_R01List(deletedTQC2070_R01List);
			removeSelectedTQC2070_R01List();
			// 원본 데이터로 복구
			setTQC2070_R01List([...originalTQC2070_R01List]);
		} else if (selectedTQC2070_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedTQC2070_R01List(selectedTQC2070_R01List);
			removeSelectedTQC2070_R01List(selectedTQC2070_R01List);
		}
	}, [selectedTQC2070_R01List, deletedTQC2070_R01List, addDeletedTQC2070_R01List, removeDeletedTQC2070_R01List, removeSelectedTQC2070_R01List, setTQC2070_R01List, originalTQC2070_R01List]);

	const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {
		// 그리드 내에서 추가된 행인지 확인
		// rowIndex가 undefined가 아니면 그리드 내에서 추가된 행
		
		const isGridInsert = rowIndex !== undefined;
		const defaultAnalysisDt = buildDefaultAnalysisDt(TQC2070_R01Filter);
		const defaultRowDataWithDate = {
			...defaultTQC2070_R01Dto,
			analysisDt: defaultAnalysisDt,
		};

		InsertRowHandler({
			event: e,
			dataList: TQC2070_R01List,
			rowData: isGridInsert ? TQC2070_R01List[rowIndex] : { ...defaultRowDataWithDate, valueIndex: -1 },
			insertRow: insertTQC2070_R01List,
			defaultRowData: defaultRowDataWithDate,
			...(isGridInsert && { setClickable }),
		});
	}, [TQC2070_R01List, TQC2070_R01Filter, insertTQC2070_R01List, setClickable]);

	// 유효성 검증 함수: 중복 일자 및 빈 일자 체크
	const validateAnalysisDt = useCallback((): boolean => {
		// 삭제되지 않은 활성 행만 필터링
		const activeList = TQC2070_R01List.filter(row => 
			!deletedTQC2070_R01List.includes(row.valueIndex) && 
			row.analysisDt
		);

		// 1. 중복 일자 체크
		const dateCountMap = new Map<string, number>();
		activeList.forEach(row => {
			const count = dateCountMap.get(row.analysisDt) || 0;
			dateCountMap.set(row.analysisDt, count + 1);
		});

		const duplicatedDates = Array.from(dateCountMap.entries())
			.filter(([_, count]) => count > 1)
			.map(([date, _]) => date);

		if (duplicatedDates.length > 0) {
			showWarning(`중복된 분석일자가 있습니다: ${duplicatedDates.join(', ')}`);
			return false;
		}

		// 2. 빈 일자 체크
		const emptyDateRows = TQC2070_R01List.filter(row => 
			!deletedTQC2070_R01List.includes(row.valueIndex) && 
			!row.analysisDt
		);

		if (emptyDateRows.length > 0) {
			showWarning('분석일자가 입력되지 않은 행이 있습니다.');
			return false;
		}

		return true;
	}, [TQC2070_R01List, deletedTQC2070_R01List]);

	const handleSaveClick = useCallback(async () => {
		// 유효성 검증
		if (!validateAnalysisDt()) {
			return;
		}

		// 신규 데이터 처리 (valueIndex 기준으로 삭제 확인 - 숫자 비교)
		const insertedList: TQC2070_R01Dto[] = TQC2070_R01List.filter(row =>
			!deletedTQC2070_R01List.includes(row.valueIndex) &&
			row.inserted === true
		);

		// 수정된 데이터 처리 (valueIndex 기준으로 삭제 확인 - 숫자 비교)
		const modifiedList: TQC2070_R01Dto[] = TQC2070_R01List.filter(row =>
			!deletedTQC2070_R01List.includes(row.valueIndex) &&
			row.isModified && !row.inserted
		);

		// 삭제 대상 리스트 (valueIndex 기준으로 확인 - 숫자 비교, API는 analysisDt로 호출)
		const deleteTargetList = TQC2070_R01List.filter(item =>
			deletedTQC2070_R01List.includes(item.valueIndex)
		);

		try {
			setLoadingSpinner(true);

			const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
				insertedList.length > 0 ? Promise.all(insertedList.map(item => createTQC2070_R01(item))) : Promise.resolve(),
				modifiedList.length > 0 ? Promise.all(modifiedList.map(item => updateTQC2070_R01(item))) : Promise.resolve(),
				deleteTargetList.length > 0 ? Promise.all(deleteTargetList.map(item => deleteTQC2070_R01(item.analysisDt))) : Promise.resolve()
			]);

			if (insertedResponse && insertedResponse.length > 0) {
				showSuccess('아연라인 액분석 결과 저장이 완료되었습니다.');
			} else if (modifiedResponse && modifiedResponse.length > 0) {
				showSuccess('아연라인 액분석 결과 수정이 완료되었습니다.');
			} else if (deleteResponse && deleteResponse.length > 0) {
				showSuccess('아연라인 액분석 결과 삭제가 완료되었습니다.');
			}

			// 저장 후 조회
			await fetchData({ messageMode: 'none' });
			
			// 삭제 목록 초기화
			removeDeletedTQC2070_R01List();
			removeSelectedTQC2070_R01List();
		} catch (error: any) {
			console.error('저장 실패:', error);
			const errorMessage = error?.response?.data?.message || error?.message || '저장에 실패했습니다.';
			showWarning(errorMessage);
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2070_R01List, deletedTQC2070_R01List, removeDeletedTQC2070_R01List, removeSelectedTQC2070_R01List, fetchData, setLoadingSpinner, validateAnalysisDt]);

	const handlePdfZincClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const yearMonth = TQC2070_R01Filter.searchAnalysisDt; // yyyy-MM 형식
			if (!yearMonth) {
				showWarning('분석일자를 선택해주세요.');
				return;
			}

			const response = await downloadPdfZinc_TQC2070_R01(yearMonth);
			const blob = new Blob([response.data], { type: 'application/pdf' });
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess('아연액 보고서 PDF가 출력되었습니다.');
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
					// showWarning("QZ 출력 실패로 브라우저 미리보기로 대체합니다.");
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess('아연액 보고서 PDF가 출력되었습니다.');
			}
		} catch (error) {
			console.error('아연액 PDF 출력 실패:', error);
			showWarning('아연액 PDF 출력에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2070_R01Filter, printPdfFromBlob, setLoadingSpinner]);

	const handlePdfNaohClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const yearMonth = TQC2070_R01Filter.searchAnalysisDt; // yyyy-MM 형식
			if (!yearMonth) {
				showWarning('분석일자를 선택해주세요.');
				return;
			}

			const response = await downloadPdfNaoh_TQC2070_R01(yearMonth);
			const blob = new Blob([response.data], { type: 'application/pdf' });
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess('가성소다액 보고서 PDF가 출력되었습니다.');
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
					// showWarning("QZ 출력 실패로 브라우저 미리보기로 대체합니다.");
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess('가성소다액 보고서 PDF가 출력되었습니다.');
			}
		} catch (error) {
			console.error('가성소다액 PDF 출력 실패:', error);
			showWarning('가성소다액 PDF 출력에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2070_R01Filter, printPdfFromBlob, setLoadingSpinner]);

	return {
		clickable, setClickable,
		fetchData,
		initContainerData,
		handleDeleteClick,
		handleInsertClick,
		handleExcelClick,
		handleSaveClick,
		handlePdfZincClick,
		handlePdfNaohClick,
	};
};

